import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer,
  Line, Bar,
  ComposedChart, Area,
  Treemap,
  XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { useLocalization } from '../contexts/LocalizationContext';
import { usePageMeta } from '../hooks/usePageMeta';
import { getUserRoles } from '../utils/auth';
import { ChartCard, KPICard, KPIGrid, PillTag } from './ui';
import RouteMap from './routePlan/RouteMap';
import { authHeaders } from './listPageStyles';
import { pick, unwrap } from './routePlan/routePlanUtils';
import './Dashboard.css';

// Funnel aşama drill-down'unda örnek satır etiketleri için statik gerçek isimler.
const REAL_PRODUCT_NAMES = [
  'OZMO Popsy', 'Luppo Kek', 'Nirvana Bitter', 'Biscolata Pia',
  'Nirvana Fındık', 'Milango', 'Stix Sütlü', 'Luppo Kakao',
  'Papita', 'Biscolata Veni', 'Mood Night', 'OZMO Fun',
];
const REAL_GROUP_NAMES = ['MİGROS Marmara', 'MİGROS İç Anadolu', 'MİGROS Ege', 'MİGROS G.Doğu Anadolu'];

// ─── Mock data ────────────────────────────────────────────────────────────────
const TREEMAP_DATA = [
  { name: 'Cola 2L', value: 8420 },
  { name: 'Cola 1L', value: 5340 },
  { name: 'Fanta 1L', value: 3220 },
  { name: 'Sprite 1L', value: 2980 },
  { name: 'Su 5L', value: 2740 },
  { name: 'Soda 200ml', value: 2150 },
  { name: 'Maden Suyu', value: 1640 },
];

const SALES_TREND = [
  { month: 'Oca', curr: 3.2, prev: 2.8 },
  { month: 'Sub', curr: 3.5, prev: 3.0 },
  { month: 'Mar', curr: 4.1, prev: 3.4 },
  { month: 'Nis', curr: 4.4, prev: 3.7 },
  { month: 'May', curr: 4.7, prev: 3.9 },
  { month: 'Haz', curr: 5.1, prev: 4.2 },
  { month: 'Tem', curr: 5.5, prev: 4.5 },
  { month: 'Agu', curr: 5.3, prev: 4.4 },
  { month: 'Eyl', curr: 5.0, prev: 4.3 },
  { month: 'Eki', curr: 5.4, prev: 4.6 },
  { month: 'Kas', curr: 5.8, prev: 4.8 },
  { month: 'Ara', curr: 6.2, prev: 5.0 },
];

const TOP_DISTRIBUTORS = [
  { name: 'HB Gıda - Bursa', revenue: 12.4 },
  { name: 'Kotiloğulları Gıda - Trabzon', revenue: 9.8 },
];

const CUSTOMER_CATEGORIES = [
  { name: 'Market', value: 31.0, revenue: 20311200, color: '#0073ea' },
  { name: 'Horeca', value: 24.5, revenue: 16051000, color: '#0ea5e9' },
  { name: 'E-Com', value: 22.0, revenue: 14412000, color: '#00c875' },
  { name: 'B2B', value: 22.5, revenue: 14739000, color: '#a13dff' },
];

const FUNNEL_STAGES = [
  { name: 'Fırsat', value: 1284, fill: '#0073ea' },
  { name: 'Teklif', value: 940, fill: '#0ea5e9' },
  { name: 'Sipariş', value: 712, fill: '#00c875' },
  { name: 'Fatura', value: 568, fill: '#fdab3d' },
  { name: 'Tahsilat', value: 472, fill: '#a13dff' },
];

const RECEIVABLES_REVENUE = [
  { month: 'Tem', revenue: 5.5, receivables: 1.6, ratio: 29 },
  { month: 'Agu', revenue: 5.3, receivables: 1.7, ratio: 32 },
  { month: 'Eyl', revenue: 5.0, receivables: 1.8, ratio: 36 },
  { month: 'Eki', revenue: 5.4, receivables: 1.7, ratio: 31 },
  { month: 'Kas', revenue: 5.8, receivables: 1.5, ratio: 26 },
  { month: 'Ara', revenue: 6.2, receivables: 1.4, ratio: 23 },
];

// Canlı müşteri kategorisi (donut) için renk paleti — backend renk göndermez.
const DONUT_COLORS = ['#0073ea', '#0ea5e9', '#00c875', '#a13dff', '#fdab3d', '#14b8a6'];
const round1 = (v) => Math.round((Number(v) || 0) * 10) / 10;

// ─── Helpers ─────────────────────────────────────────────────────────────────
function formatTRY(value) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 0 }).format(value);
}

function formatNumber(value) {
  return new Intl.NumberFormat('tr-TR').format(value);
}

function IconRevenue() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 2v20M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </svg>
  );
}

function IconOrders() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  );
}

function IconCollection() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /><path d="M6 15h4" />
    </svg>
  );
}

function IconCustomers() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.9" /><path d="M16 3.1a4 4 0 0 1 0 7.8" />
    </svg>
  );
}

function IconBolt() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M13 2L3 14h8l-1 8 11-13h-8l0-7z" />
    </svg>
  );
}

function useChartTooltip() {
  const [tip, setTip] = useState(null);
  const show = useCallback((e, text) => {
    const host = e.currentTarget.closest('.dashboard-funnel-wrap, .dashboard-bubble-wrap, .dashboard-treemap-wrap');
    if (!host) return;
    const rect = host.getBoundingClientRect();
    setTip({ x: e.clientX - rect.left, y: e.clientY - rect.top, text });
  }, []);
  const hide = useCallback(() => setTip(null), []);
  return { tip, show, hide };
}

function ChartTooltip({ tip }) {
  if (!tip) return null;
  return (
    <div className="dashboard-chart-tooltip" style={{ left: tip.x, top: tip.y }}>
      {tip.text}
    </div>
  );
}

const FUNNEL_COLORS = [
  'rgba(0,115,234,0.95)',
  'rgba(14,165,233,0.92)',
  'rgba(16,185,129,0.86)',
  'rgba(253,171,61,0.86)',
  'rgba(139,92,246,0.86)',
];

function RevOpsFunnel({ stages, onStageClick }) {
  const { tip, show, hide } = useChartTooltip();
  const W = 1000;
  const H = 520;
  const gap = 10;
  const n = stages.length;
  if (!n) return null;
  const stepH = (H - gap * (n - 1)) / n;
  const minW = 260;
  const maxW = 920;
  const max = Math.max(...stages.map((s) => s.value)) || 1;
  const widths = stages.map((s) => minW + (s.value / max) * (maxW - minW));
  return (
    <div className="dashboard-funnel-wrap" onMouseLeave={hide}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet" role="img" aria-label="Contract Funnel">
        <defs>
          <filter id="fnShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="rgba(15,23,42,0.20)" />
          </filter>
        </defs>
        {stages.map((s, i) => {
          const topW = widths[i];
          const botW = widths[i + 1] ?? Math.max(minW, topW * 0.88);
          const y = i * (stepH + gap);
          const xTop = (W - topW) / 2;
          const xBot = (W - botW) / 2;
          const y2 = y + stepH;
          const cx = W / 2;
          const cy = y + stepH / 2;
          const fill = FUNNEL_COLORS[i % FUNNEL_COLORS.length];
          const stroke = 'rgba(15, 23, 42, 0.45)';
          const path = `M ${xTop} ${y} L ${xTop + topW} ${y} L ${xBot + botW} ${y2} L ${xBot} ${y2} Z`;
          const label = `${s.name} · ${formatNumber(s.value)} opportunities`;
          const onMove = (e) => show(e, label);
          return (
            <g
              key={s.name}
              style={{ cursor: 'pointer' }}
              onMouseEnter={onMove}
              onMouseMove={onMove}
              onClick={() => onStageClick && onStageClick(s)}
            >
              <path d={path} fill={fill} opacity={0.88} filter="url(#fnShadow)" />
              <path d={path} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.2} />
              <text
                x={cx}
                y={cy - 9}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="rgba(255,255,255,0.98)"
                fontSize={20}
                fontWeight="900"
                fontFamily="Manrope, ui-sans-serif, system-ui"
                stroke={stroke}
                strokeWidth={5}
                paintOrder="stroke"
              >
                {s.name}
              </text>
              <text
                x={cx}
                y={cy + 14}
                textAnchor="middle"
                dominantBaseline="middle"
                fill="rgba(255,255,255,0.92)"
                fontSize={16}
                fontWeight="900"
                fontFamily="Manrope, ui-sans-serif, system-ui"
                stroke={stroke}
                strokeWidth={4}
                paintOrder="stroke"
              >
                {formatNumber(s.value)}
              </text>
            </g>
          );
        })}
      </svg>
      <ChartTooltip tip={tip} />
    </div>
  );
}

function CustomerDonut({ items, active, onHover }) {
  if (!items.length) return <EmptyChart message="Bu dönemde müşteri kategorisi cirosu yok." />;
  const total = items.reduce((s, x) => s + x.value, 0) || 1;
  let cum = 0;
  const stops = items
    .map((it) => {
      const start = (cum / total) * 100;
      cum += it.value;
      const end = (cum / total) * 100;
      return `${it.color} ${start}% ${end}%`;
    })
    .join(', ');
  const a = items[active];
  return (
    <div className="dashboard-donut-mockup">
      <div className="dashboard-donut-disc" style={{ background: `conic-gradient(${stops})` }}>
        <div className="dashboard-donut-center-card">
          <small>{a.name}</small>
          <strong>{a.value.toFixed(2)}%</strong>
          <small className="muted">{formatTRY(a.revenue)}</small>
        </div>
      </div>
      <div className="dashboard-donut-legend">
        {items.map((it, idx) => (
          <button
            type="button"
            key={it.name}
            className={`dashboard-donut-legend-row${idx === active ? ' active' : ''}`}
            onMouseEnter={() => onHover(idx)}
            onFocus={() => onHover(idx)}
          >
            <span className="dashboard-donut-dot" style={{ background: it.color }} />
            <span className="dashboard-donut-legend-label">{it.name}</span>
            <small>{it.value.toFixed(1)}%</small>
          </button>
        ))}
      </div>
    </div>
  );
}

function DistributorBars({ items }) {
  if (!items.length) return <EmptyChart message="Bu dönemde distribütör cirosu yok." />;
  const max = Math.max(...items.map((d) => d.revenue)) || 1;
  return (
    <div className="dashboard-bars">
      {items.map((d, idx) => (
        <div className="dashboard-bar-row" key={d.name}>
          <div className="dashboard-bar-rank">{idx + 1}</div>
          <div className="dashboard-bar-label" title={d.name}>{d.name}</div>
          <div className="dashboard-bar-track">
            <div className="dashboard-bar-fill" style={{ width: `${(d.revenue / max) * 100}%` }} />
          </div>
          <div className="dashboard-bar-val">₺{d.revenue.toFixed(1)}M</div>
        </div>
      ))}
    </div>
  );
}

function ProductTreemap({ items }) {
  const { tip, show, hide } = useChartTooltip();
  if (!items.length) return <EmptyChart message="Bu dönemde ürün satışı yok." />;
  return (
    <div className="dashboard-treemap-wrap" onMouseLeave={hide}>
      <ResponsiveContainer width="100%" height="100%">
        <Treemap
          data={items}
          dataKey="value"
          content={<TreemapTile onTileMove={show} />}
          isAnimationActive={false}
        />
      </ResponsiveContainer>
      <ChartTooltip tip={tip} />
    </div>
  );
}

// Veri olmayan widget'lar için boş durum (yeni/boş tenant'ta sahte demo yerine).
function EmptyChart({ message = 'Henüz veri yok' }) {
  return (
    <div className="dashboard-empty-chart" role="status">
      <div>
        <strong>Veri yok</strong>
        <p>{message}</p>
      </div>
    </div>
  );
}

function MapPlaceholder({ hint }) {
  return (
    <div className="dashboard-map-placeholder" aria-label="Müşteri konum haritası">
      <div>
        <strong>Harita hazırlanıyor</strong>
        <p>Müşteri konumları yükleniyor… ({hint || 'lütfen bekleyin'})</p>
      </div>
    </div>
  );
}

const TREEMAP_PALETTE = ['#1d4ed8', '#0ea5e9', '#10b981', '#fdab3d', '#8b5cf6', '#14b8a6', '#00c875', '#3b82f6'];

// Etiketi tile genişliğine göre en çok 2 satıra sar; taşarsa kısalt (…).
function wrapTwoLines(text, maxChars) {
  const words = String(text).trim().split(/\s+/);
  const lines = ['', ''];
  let li = 0;
  for (const w of words) {
    const cand = lines[li] ? `${lines[li]} ${w}` : w;
    if (cand.length <= maxChars || !lines[li]) {
      lines[li] = cand;
    } else if (li === 0) {
      li = 1;
      lines[1] = w;
    } else {
      lines[1] = `${lines[1]} ${w}`;
    }
  }
  return lines
    .filter(Boolean)
    .map((l) => (l.length > maxChars ? `${l.slice(0, Math.max(1, maxChars - 1))}…` : l))
    .slice(0, 2);
}

function TreemapTile(props) {
  const { x, y, width, height, name, value, index, depth, onTileMove } = props;
  if (depth !== 1) return null;
  if (!width || !height || width < 4 || height < 4) return null;
  const fill = TREEMAP_PALETTE[index % TREEMAP_PALETTE.length];
  // Mockup formula:
  //   nameSize = max(18, min(28, min(w,h)/6))   (viewBox 1000×520, ~0.4 ekran scale)
  //   valueSize = max(16, min(22, min(w,h)/7))
  // Bizim pixel-koordinatlı eşdeğeri: aynı /6 ve /7, min/max ekran pixel'inde:
  const minDim = Math.min(width, height);
  const showName = width > 36 && height > 20;
  const showValue = width > 80 && height > 50;
  const nameSize = Math.max(7, Math.min(11, Math.floor(minDim / 6)));
  const valueSize = Math.max(6, Math.min(9, Math.floor(minDim / 7)));
  const cx = x + width / 2;
  const cy = y + height / 2;
  // İsmi tile genişliğine göre 2 satıra sar; dikey olarak isim+değer bloğunu ortala.
  const maxChars = Math.max(4, Math.floor((width - 10) / (nameSize * 0.6)));
  const nameLines = showName ? wrapTwoLines(name, maxChars) : [];
  const lineGap = 1;
  const nameH = nameLines.length * nameSize + Math.max(0, nameLines.length - 1) * lineGap;
  const valueH = showValue ? valueSize + 4 : 0;
  const blockTop = cy - (nameH + valueH) / 2;
  const firstLineY = blockTop + nameSize / 2;
  const valueY = blockTop + nameH + lineGap + valueSize / 2;
  const handleMove = onTileMove ? (e) => onTileMove(e, `${name} · ₺${value}K`) : undefined;
  return (
    <g style={{ cursor: 'pointer' }} onMouseEnter={handleMove} onMouseMove={handleMove}>
      <rect x={x + 1.5} y={y + 1.5} width={Math.max(0, width - 3)} height={Math.max(0, height - 3)} rx={12} ry={12} fill={fill} opacity={0.86} />
      <rect x={x + 1.5} y={y + 1.5} width={Math.max(0, width - 3)} height={Math.max(0, height - 3)} rx={12} ry={12} fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth={1.5} />
      {showName && nameLines.length > 0 && (
        <text
          x={cx}
          y={firstLineY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.98)"
          fontSize={nameSize}
          fontWeight="900"
          fontFamily="Manrope, ui-sans-serif, system-ui"
          stroke="rgba(15,23,42,0.45)"
          strokeWidth={4}
          paintOrder="stroke"
        >
          {nameLines.map((ln, i) => (
            <tspan key={i} x={cx} dy={i === 0 ? 0 : nameSize + lineGap}>{ln}</tspan>
          ))}
        </text>
      )}
      {showValue && (
        <text
          x={cx}
          y={valueY}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="rgba(255,255,255,0.92)"
          fontSize={valueSize}
          fontWeight="900"
          fontFamily="Manrope, ui-sans-serif, system-ui"
          stroke="rgba(15,23,42,0.45)"
          strokeWidth={3}
          paintOrder="stroke"
        >
          {`₺${value}K`}
        </text>
      )}
    </g>
  );
}

// ─── Kart detay popup'ları (mevcut veriyi tablo olarak gösterir) ───────────────
function detailSalesTrend(title, trend = SALES_TREND) {
  return {
    title,
    subtitle: 'Aylık trend · Bu yıl vs geçen yıl (₺M)',
    columns: [
      { key: 'month', label: 'Ay' },
      { key: 'curr', label: 'Bu Yıl', align: 'right', format: (v) => `₺${v}M` },
      { key: 'prev', label: 'Geçen Yıl', align: 'right', format: (v) => `₺${v}M` },
      { key: 'delta', label: 'Δ (YoY)', align: 'right', format: (v) => `${v > 0 ? '+' : ''}${v.toFixed(1)}M` },
    ],
    rows: trend.map((r) => ({ ...r, delta: r.curr - r.prev })),
  };
}

function detailFunnel(title, stages = FUNNEL_STAGES) {
  const base = stages[0]?.value || 1;
  return {
    title,
    subtitle: 'Müşteri → Sipariş → Fatura → Tahsilat hunisi',
    columns: [
      { key: 'name', label: 'Aşama' },
      { key: 'value', label: 'Adet', align: 'right', format: (v) => formatNumber(v) },
      { key: 'conv', label: 'Dönüşüm', align: 'right', format: (v) => `%${v.toFixed(1)}` },
    ],
    rows: stages.map((r) => ({ ...r, conv: (r.value / base) * 100 })),
  };
}

// Tek bir funnel aşamasına tıklanınca o aşamadaki kayıtların listesi (gerçek isimlerle).
function detailFunnelStage(stage) {
  const count = Number(stage.value) || 0;
  const shown = Math.min(count, 12);
  const rows = Array.from({ length: shown }, (_, i) => ({
    no: i + 1,
    customer: REAL_GROUP_NAMES[i % REAL_GROUP_NAMES.length],
    product: REAL_PRODUCT_NAMES[i % REAL_PRODUCT_NAMES.length],
    amount: 18000 + ((i * 37) % 60) * 1000,
  }));
  return {
    title: `${stage.name} · ${formatNumber(count)} kayıt`,
    subtitle: `İlk ${shown} kayıt gösteriliyor`,
    columns: [
      { key: 'no', label: '#', align: 'right' },
      { key: 'customer', label: 'Müşteri Grubu' },
      { key: 'product', label: 'Ürün' },
      { key: 'amount', label: 'Tutar', align: 'right', format: (v) => formatTRY(v) },
    ],
    rows,
  };
}

function detailReceivables(title, rows = RECEIVABLES_REVENUE) {
  return {
    title,
    subtitle: 'Aylık ciro, alacak ve oran',
    columns: [
      { key: 'month', label: 'Ay' },
      { key: 'revenue', label: 'Ciro', align: 'right', format: (v) => `₺${v}M` },
      { key: 'receivables', label: 'Alacak', align: 'right', format: (v) => `₺${v}M` },
      { key: 'ratio', label: 'Oran', align: 'right', format: (v) => `%${v}` },
    ],
    rows,
  };
}

function detailCustomerCategories(title, items = CUSTOMER_CATEGORIES) {
  return {
    title,
    subtitle: 'Kategori bazlı dağılım (YTD)',
    columns: [
      { key: 'name', label: 'Kategori' },
      { key: 'value', label: 'Pay', align: 'right', format: (v) => `%${v}` },
      { key: 'revenue', label: 'Ciro', align: 'right', format: (v) => formatTRY(v) },
    ],
    rows: items,
  };
}

function detailTreemap(items = TREEMAP_DATA) {
  return {
    title: 'Product-Based Sales · Ürün Kırılımı',
    subtitle: 'Son 30 gün satış hacmi',
    columns: [
      { key: 'name', label: 'Ürün' },
      { key: 'value', label: 'Hacim', align: 'right', format: (v) => `₺${formatNumber(v)}K` },
    ],
    rows: [...items].sort((a, b) => b.value - a.value),
  };
}

function detailDistributors(items = TOP_DISTRIBUTORS) {
  return {
    title: 'Distribütörler · YTD Gelir',
    subtitle: 'Distribütör kırılımı',
    columns: [
      { key: 'name', label: 'Distribütör' },
      { key: 'revenue', label: 'Gelir', align: 'right', format: (v) => `₺${v}M` },
    ],
    rows: items,
  };
}

function detailHeatmap(count = 0) {
  return {
    title: 'Türkiye Satış Haritası',
    subtitle: 'OpenStreetMap · satış yoğunluğu (canlı)',
    columns: [{ key: 'info', label: 'Bilgi' }],
    rows: [{ info: `Konumu olan ${formatNumber(count)} müşteri, sipariş net tutarı toplamına göre ısı haritasında ağırlıklandırılır. Sipariş verisi yoksa müşteri yoğunluğu gösterilir. Veri /api/customers/sales-heatmap üzerinden canlı çekilir.` }],
  };
}

function DashboardDetailModal({ detail, onClose }) {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, onClose]);

  if (!detail) return null;
  const { title, subtitle, columns, rows } = detail;
  return (
    <div className="dashboard-modal-overlay" onClick={onClose}>
      <div className="dashboard-modal" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <div className="dashboard-modal-head">
          <div>
            <h3 className="dashboard-modal-title">{title}</h3>
            {subtitle && <p className="dashboard-modal-sub">{subtitle}</p>}
          </div>
          <button type="button" className="dashboard-modal-close" onClick={onClose} aria-label="Kapat">×</button>
        </div>
        <div className="dashboard-modal-body">
          <table className="dashboard-modal-table">
            <thead>
              <tr>{columns.map((c) => <th key={c.key} style={{ textAlign: c.align || 'left' }}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ textAlign: c.align || 'left' }}>
                      {c.format ? c.format(row[c.key], row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
export function Dashboard() {
  const { t } = useLocalization();
  usePageMeta(
    t('dashboard.title', 'Dashboard'),
    t('dashboard.subtitle', 'Financial Profile 360 · Sales operations overview'),
  );

  const [telemetryData, setTelemetryData] = useState(null);
  const [overview, setOverview] = useState(null);
  const [activeCategory, setActiveCategory] = useState(0);
  const [detail, setDetail] = useState(null);
  const [mapPoints, setMapPoints] = useState([]);
  const [mapLoading, setMapLoading] = useState(true);
  const userRoles = getUserRoles();
  const canViewBackendTelemetry = userRoles.includes('MANAGER') || userRoles.includes('ADMIN');

  // Task #136: Tüm widget'ların gerçek verisini tek seferde çek. Yanıt gelene kadar
  // (veya hata/boş veri durumunda) statik mock veriler fallback olarak kullanılır.
  useEffect(() => {
    let cancelled = false;
    fetch('/api/dashboard/overview', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled) setOverview(j ? unwrap(j) : null); })
      .catch(() => { if (!cancelled) setOverview(null); });
    return () => { cancelled = true; };
  }, []);

  // ── Canlı veriyi widget'ların beklediği şekle/ölçeğe (₺M / ₺K) çevir; yoksa mock ──
  const salesTrendData = useMemo(() => {
    const t = overview?.salesTrend;
    if (!t || !t.length) return SALES_TREND;
    return t.map((m) => ({ month: m.label, curr: round1(m.curr / 1e6), prev: round1(m.prev / 1e6) }));
  }, [overview]);

  // Bu 3 widget GERÇEK veriye bağlıdır; veri yoksa (yeni/boş tenant) mock'a DÜŞMEZ,
  // boş dizi döner ve bileşen "veri yok" durumunu gösterir (sahte demo gösterilmez).
  const liveTreemapData = useMemo(() => {
    const p = overview?.topProducts;
    if (!Array.isArray(p)) return [];
    return p.map((x) => ({ name: x.name, value: Math.round((Number(x.value) || 0) / 1000) }));
  }, [overview]);

  const liveCategoriesData = useMemo(() => {
    const c = overview?.customerCategories;
    if (!Array.isArray(c)) return [];
    return c.map((x, i) => ({
      name: x.name,
      value: Number(x.share) || 0,
      revenue: Number(x.revenue) || 0,
      color: DONUT_COLORS[i % DONUT_COLORS.length],
    }));
  }, [overview]);

  const liveDistributorsData = useMemo(() => {
    const d = overview?.topDistributors;
    if (!Array.isArray(d)) return [];
    return d.map((x) => ({ name: x.name, revenue: round1(x.value / 1e6) }));
  }, [overview]);

  const funnelData = useMemo(() => {
    const f = overview?.funnel;
    if (!f || !f.length) return FUNNEL_STAGES;
    return f.map((s, i) => ({ name: s.name, value: Number(s.value) || 0, fill: FUNNEL_COLORS[i % FUNNEL_COLORS.length] }));
  }, [overview]);

  const receivablesData = useMemo(() => {
    const r = overview?.receivables;
    if (!r || !r.length) return RECEIVABLES_REVENUE;
    return r.map((x) => ({
      month: x.label,
      revenue: round1(x.revenue / 1e6),
      receivables: round1(x.receivables / 1e6),
      ratio: Math.round(Number(x.ratio) || 0),
    }));
  }, [overview]);

  const revenueDelta = overview?.revenueDeltaPct;
  const hasRevenueDelta = revenueDelta != null;

  // Satış yoğunluğu ısı haritası: konumu olan müşteriler + sipariş net tutarı (ağırlık).
  // Ağırlık 0..1'e normalize edilip ısı katmanına intensity olarak verilir; tüm tutarlar
  // 0 ise (sipariş verisi yoksa) müşteri yoğunluğuna düşer (uniform intensity).
  useEffect(() => {
    let cancelled = false;
    fetch('/api/customers/sales-heatmap', { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (cancelled) return;
        const rows = unwrap(j);
        const raw = (Array.isArray(rows) ? rows : [])
          .map((r) => ({
            lat: Number(pick(r, 'lat', 'latitude', 'Latitude')),
            lng: Number(pick(r, 'lng', 'longitude', 'Longtitude', 'Longitude')),
            label: pick(r, 'title', 'Title'),
            weight: Number(pick(r, 'weight', 'Weight')) || 0,
          }))
          .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0));
        const maxW = Math.max(0, ...raw.map((p) => p.weight));
        const pts = raw.map((p) => ({
          ...p,
          // Satış varsa tutarla orantılı; veri yoksa hepsi eşit yoğunlukta görünsün.
          intensity: maxW > 0 ? Math.max(0.15, p.weight / maxW) : 0.5,
        }));
        setMapPoints(pts);
      })
      .catch(() => { if (!cancelled) setMapPoints([]); })
      .finally(() => { if (!cancelled) setMapLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!canViewBackendTelemetry) return;
    const token = localStorage.getItem('token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    fetch('/api/dashboard/manager/telemetry', { headers })
      .then(r => (r.ok ? r.json() : null))
      .then(j => setTelemetryData(j?.data ?? j))
      .catch(() => setTelemetryData(null));
  }, [canViewBackendTelemetry]);

  const slowCount = telemetryData?.slowHandlerCountLast24Hours ?? null;
  const activeCat = liveCategoriesData[activeCategory] || liveCategoriesData[0];

  const telemetryDetail = () => ({
    title: 'Yavaş Handler · Telemetri (24s)',
    subtitle: 'Backend performans metrikleri',
    columns: [{ key: 'metric', label: 'Metrik' }, { key: 'value', label: 'Değer', align: 'right' }],
    rows: [
      { metric: 'Yavaş handler sayısı (24s)', value: slowCount != null ? formatNumber(slowCount) : '—' },
      { metric: 'En yüksek süre', value: `${telemetryData?.maxSlowHandlerDurationMs ?? 0} ms` },
    ],
  });

  return (
    <div className="dashboard">
      <KPIGrid>
        <KPICard
          label="Toplam Ciro"
          value={overview ? formatTRY(overview.totalRevenue) : formatTRY(47_500_000)}
          delta={hasRevenueDelta ? `${revenueDelta > 0 ? '+' : ''}${revenueDelta}%` : (overview ? undefined : '+12.4%')}
          deltaDirection={hasRevenueDelta ? (revenueDelta >= 0 ? 'up' : 'down') : 'up'}
          hint={overview ? 'bu yıl (YTD)' : 'last 7 days'}
          icon={<IconRevenue />}
          accentColor="rgba(0, 115, 234, 0.45)"
          onClick={() => setDetail(detailSalesTrend('Toplam Ciro · Aylık Kırılım', salesTrendData))}
        />
        <KPICard
          label="Açık Sipariş"
          value={overview ? formatNumber(overview.openOrders) : formatNumber(1284)}
          delta={overview ? undefined : '+5.1%'}
          deltaDirection="up"
          hint={overview ? 'faturalanmamış sipariş' : 'last 7 days'}
          icon={<IconOrders />}
          accentColor="rgba(14, 165, 233, 0.45)"
          onClick={() => setDetail(detailFunnel('Açık Sipariş · Pipeline Aşamaları', funnelData))}
        />
        <KPICard
          label="Tahsilat Oranı"
          value={overview ? `%${(Number(overview.collectionRate) || 0).toFixed(1)}` : '%87.4'}
          delta={overview ? undefined : '+2.1%'}
          deltaDirection="up"
          hint={overview ? 'tahsil / fatura (YTD)' : 'margin improvement'}
          icon={<IconCollection />}
          accentColor="rgba(0, 200, 117, 0.45)"
          onClick={() => setDetail(detailReceivables('Tahsilat Oranı · Aylık', receivablesData))}
        />
        <KPICard
          label="Aktif Müşteri"
          value={overview ? formatNumber(overview.activeCustomers) : formatNumber(3672)}
          delta={overview ? undefined : '+4.3%'}
          deltaDirection="up"
          hint={overview ? `Toplam: ${formatNumber(overview.totalCustomers)}` : `Total: ${formatNumber(3672)}`}
          icon={<IconCustomers />}
          accentColor="rgba(139, 92, 246, 0.45)"
          onClick={() => setDetail(detailCustomerCategories('Aktif Müşteri · Kategori Dağılımı', liveCategoriesData))}
        />
        <KPICard
          label="Yavaş Handler (24s)"
          value={slowCount != null ? formatNumber(slowCount) : '—'}
          hint={`max ${telemetryData?.maxSlowHandlerDurationMs ?? 0}ms`}
          icon={<IconBolt />}
          accentColor="rgba(253, 171, 61, 0.45)"
          onClick={() => setDetail(telemetryDetail())}
        />
      </KPIGrid>

      <div className="dashboard-grid-2">
        <ChartCard title="Product-Based Sales" subtitle="Treemap · Bu yıl (YTD)" actions={<PillTag>Treemap</PillTag>} onClick={() => setDetail(detailTreemap(liveTreemapData))}>
          <ProductTreemap items={liveTreemapData} />
        </ChartCard>

        <ChartCard
          title="YTD Total Sales"
          subtitle="Aylık Trend (Bu Yıl vs Geçen Yıl) · ₺"
          actions={hasRevenueDelta ? <PillTag tone={revenueDelta >= 0 ? 'success' : 'danger'}>{`${revenueDelta > 0 ? '+' : ''}${revenueDelta}% YoY`}</PillTag> : <PillTag tone="success">+24% YoY</PillTag>}
          onClick={() => setDetail(detailSalesTrend('YTD Total Sales · Aylık Trend', salesTrendData))}
        >
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={salesTrendData} margin={{ top: 14, right: 18, bottom: 0, left: 18 }}>
              <defs>
                <linearGradient id="gLine" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="rgba(0,115,234,0.95)" />
                  <stop offset="55%" stopColor="rgba(14,165,233,0.95)" />
                  <stop offset="100%" stopColor="rgba(16,185,129,0.85)" />
                </linearGradient>
                <linearGradient id="gFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(0,115,234,0.18)" />
                  <stop offset="100%" stopColor="rgba(0,115,234,0)" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(15,23,42,0.06)" vertical={false} strokeDasharray="0" />
              <XAxis
                dataKey="month"
                stroke="rgba(100,116,139,0.9)"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                tick={{ fontWeight: 800 }}
              />
              <YAxis hide domain={['dataMin - 0.4', 'dataMax + 0.4']} />
              <Tooltip formatter={(v, n) => [`₺${v}M`, n]} />
              <Area type="monotone" dataKey="curr" stroke="none" fill="url(#gFill)" fillOpacity={1} isAnimationActive={false} />
              <Line
                type="monotone"
                dataKey="prev"
                name="Last Year"
                stroke="rgba(100,116,139,0.70)"
                strokeWidth={3.1}
                strokeDasharray="7 7"
                strokeLinecap="round"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="curr"
                name="This Year"
                stroke="url(#gLine)"
                strokeWidth={3.4}
                strokeLinecap="round"
                dot={{ r: 4.2, fill: '#fff', stroke: 'rgba(0,115,234,0.7)', strokeWidth: 2 }}
                activeDot={{ r: 5.5, fill: '#fff', stroke: 'rgba(0,115,234,0.85)', strokeWidth: 2.5 }}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Distribütörler"
          subtitle="YTD Revenue · Bölgesel kırılım"
          actions={<PillTag>Distribütör</PillTag>}
          onClick={() => setDetail(detailDistributors(liveDistributorsData))}
        >
          <DistributorBars items={liveDistributorsData} />
        </ChartCard>
      </div>

      <div className="dashboard-grid-3">
        <ChartCard
          title="Customer Categories"
          subtitle="Dağılım (YTD)"
          actions={<PillTag>{activeCat?.name ?? '—'}</PillTag>}
          onClick={() => setDetail(detailCustomerCategories('Customer Categories · Dağılım', liveCategoriesData))}
        >
          <CustomerDonut items={liveCategoriesData} active={activeCategory} onHover={setActiveCategory} />
        </ChartCard>

        <ChartCard
          title="RevOps Contract"
          subtitle="Müşteri → Sipariş → Fatura → Tahsilat · Bu yıl"
          actions={<PillTag>Funnel</PillTag>}
          onClick={() => setDetail(detailFunnel('RevOps Contract · Funnel', funnelData))}
        >
          <RevOpsFunnel stages={funnelData} onStageClick={(s) => setDetail(detailFunnelStage(s))} />
        </ChartCard>
      </div>

      <div className="dashboard-grid-1">
        <ChartCard
          title="Receivables / Revenue Ratio"
          subtitle="Aylık · Ciro vs Alacak + Oran"
          actions={<PillTag>Aylık</PillTag>}
          onClick={() => setDetail(detailReceivables('Receivables / Revenue · Aylık', receivablesData))}
        >
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={receivablesData} margin={{ top: 14, right: 18, bottom: 0, left: 12 }}>
              <defs>
                <linearGradient id="gBarRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(0,115,234,0.95)" />
                  <stop offset="100%" stopColor="rgba(0,115,234,0.55)" />
                </linearGradient>
                <linearGradient id="gBarCari" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="rgba(14,165,233,0.95)" />
                  <stop offset="100%" stopColor="rgba(14,165,233,0.55)" />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="rgba(15,23,42,0.06)" vertical={false} strokeDasharray="0" />
              <XAxis
                dataKey="month"
                stroke="rgba(100,116,139,0.9)"
                fontSize={11}
                tickLine={false}
                axisLine={{ stroke: 'rgba(15,23,42,0.08)' }}
                tick={{ fontWeight: 800 }}
              />
              <YAxis yAxisId="left" hide />
              <YAxis yAxisId="right" orientation="right" hide />
              <Tooltip />
              <Bar yAxisId="left" dataKey="revenue" fill="url(#gBarRev)" name="Revenue" radius={[8, 8, 0, 0]} isAnimationActive={false} />
              <Bar yAxisId="left" dataKey="receivables" fill="url(#gBarCari)" name="Receivables" radius={[8, 8, 0, 0]} isAnimationActive={false} />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="ratio"
                stroke="rgba(253,171,61,0.95)"
                strokeWidth={3}
                strokeLinecap="round"
                dot={{ r: 4, fill: '#fff', stroke: 'rgba(253,171,61,0.7)', strokeWidth: 2 }}
                activeDot={{ r: 5.5, fill: '#fff', stroke: 'rgba(253,171,61,0.85)', strokeWidth: 2.5 }}
                name="Ratio %"
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title="Türkiye Satış Haritası"
          subtitle={mapLoading ? 'OpenStreetMap · yükleniyor…' : `OpenStreetMap · satış yoğunluğu · ${formatNumber(mapPoints.length)} müşteri`}
          actions={<PillTag tone="success">Canlı</PillTag>}
          onClick={() => setDetail(detailHeatmap(mapPoints.length))}
        >
          {mapLoading
            ? <MapPlaceholder hint="Yükleniyor…" />
            : <RouteMap points={mapPoints} height={320} heatmap />}
        </ChartCard>
      </div>

      <DashboardDetailModal detail={detail} onClose={() => setDetail(null)} />
    </div>
  );
}
